// src/components/support/forms/EarlyResume.js
import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import SpinnerLoading from "../../common/SpinnerLoading";
import FootprintDatePicker from "../../common/FootprintDatePicker";
import moment from "moment";

import { resumeFromBreak } from "../../../queryfunctions/queryfunctions";
import { useRequestProcessor } from "../../../requestProcessor";
import { SERVER_DATE_FORMAT } from "../../home/Home";
import { alertModal, appConstants, selectedSupportItem } from "../../../atomstore/store";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";


const DEFAULT_FORMAT = "MMM DD, YYYY";
const DEFAULT_TAKE_BREAK_MAX_OFFSET_DAYS = 30;

const getMaxAllowedDateFromPauseStart = (startDate, offsetDays = DEFAULT_TAKE_BREAK_MAX_OFFSET_DAYS) => {
    const start = moment(startDate, SERVER_DATE_FORMAT);
    if (!start.isValid()) return null;
    if (start.date() === 1) return start.clone().endOf("month");
    return start.clone().add(Math.max(offsetDays - 1, 0), "days");
};

const EarlyResume = () => {
    const navigate = useNavigate();
    const [item] = useAtom(selectedSupportItem);
    const [constants] = useAtom(appConstants);
    const [, setAlertModal] = useAtom(alertModal);
    const takeBreakMaxOffsetDaysRaw = Number(constants?.takeBreakMaxOffsetDays ?? DEFAULT_TAKE_BREAK_MAX_OFFSET_DAYS);
    const takeBreakMaxOffsetDays = Number.isFinite(takeBreakMaxOffsetDaysRaw)
        ? takeBreakMaxOffsetDaysRaw
        : DEFAULT_TAKE_BREAK_MAX_OFFSET_DAYS;

    const [newDate, setNewDate] = useState("");
    const [error, setError] = useState("");

    const { useMutationWrapper } = useRequestProcessor();

    const resumeMutation = useMutationWrapper(
        ["resumeFromBreak"],
        () => resumeFromBreak(item.request_id, newDate, undefined, item?.end_date)
    );

    const validate = () => {
        if (!newDate) {
            setError("Please select the new date");
            return false;
        }

        const pauseStart = moment(item?.date, SERVER_DATE_FORMAT);
        const oldEnd = moment(item?.end_date, SERVER_DATE_FORMAT);
        const selected = moment(newDate);
        const maxAllowed = getMaxAllowedDateFromPauseStart(item?.date, takeBreakMaxOffsetDays);
        const effectiveMax = moment.min(
            oldEnd.isValid() ? oldEnd.clone().subtract(1, "day") : moment("9999-12-31"),
            maxAllowed || moment("9999-12-31")
        );

        if (pauseStart.isValid() && selected.isValid() && selected.isBefore(pauseStart, "day")) {
            setError(`New date cannot be before ${pauseStart.format(DEFAULT_FORMAT)}`);
            return false;
        }
        if (effectiveMax.isValid() && selected.isValid() && selected.isAfter(effectiveMax, "day")) {
            setError(`New date cannot be after ${effectiveMax.format(DEFAULT_FORMAT)}`);
            return false;
        }
        return true;
    };

    const submitRequest = () => {
        if (!validate()) return;

        const pauseStart = moment(item?.date, SERVER_DATE_FORMAT);
        const resumeDate = moment(newDate);
        const pauseDays = resumeDate.diff(pauseStart, "days");

        if (pauseStart.isValid() && resumeDate.isValid() && pauseDays < 30) {
            setAlertModal({
                state: true,
                type: "Warning",
                isConfirm: true,
                message:
                    constants?.earlyResumeInfoBanner ||
                    "If the service is resumed early, the system may generate an invoice if the pause period is less than 30 days",
                onOkPressed: () => resumeMutation.mutate(),
                onCancelPressed: () => { },
                okText: "Proceed",
                cancelText: "Cancel",
            });
            return;
        }

        resumeMutation.mutate();
    };

    if (!item) {
        return (
            <div className="p-4">
                <p className="text-center text-gray-600">Unable to load data</p>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {resumeMutation?.isLoading && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Resume Early</h2>
            </div>

            <div className="p-4 space-y-4">

                {/* Current End Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current End Date
                    </label>
                    <FootprintDatePicker
                        defaultDate={moment(item?.end_date).format(DEFAULT_FORMAT)}
                        inputProps={{ disabled: true }}
                        disabled={true}
                    />
                </div>

                {/* New Resume Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Date
                    </label>
                    <FootprintDatePicker
                        minimumDate={moment(item?.date, SERVER_DATE_FORMAT)}
                        maximumDate={moment.min(
                            moment(item?.end_date, SERVER_DATE_FORMAT).subtract(1, "days"),
                            getMaxAllowedDateFromPauseStart(item?.date, takeBreakMaxOffsetDays) || moment("9999-12-31")
                        )}
                        onChange={(date) => {
                            setNewDate(date);
                            setError("");
                        }}
                        inputProps={{ placeholder: "Select date" }}
                        timeFormat={false}
                    />
                </div>

                {/* Error */}
                {error && <p className="text-red-500 text-sm">{error}</p>}

                {/* Submit */}
                <button
                    onClick={submitRequest}
                    disabled={resumeMutation.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Request
                </button>
            </div>
        </div>
    );
};

export default EarlyResume;
